import {
	addMonths,
	addYears,
	differenceInBusinessDays,
	differenceInCalendarDays,
	isValid,
	lastDayOfMonth,
	parse,
	parseISO,
	startOfMonth,
	subDays,
} from "date-fns";

export const ISO8601_FORMAT = "yyyy-MM-dd";

export function prepareDayFormatForRegex(format: string) {
	return (
		format
			// TODO: Enable this regex later, we disable it due to the limitation of look ahead and after in older browsers
			// .replace(/((?<![dm])[dm](?![dm]))/g, "$1$1?")
			.replace(/[.+|]\\/g, "\\$&")
			.replace(/\w/g, "[0-9]")
	);
}

export function createDayRegex(format: string) {
	return new RegExp(`^(${format})(T.+)?$`);
}

export const dayISORegex = createDayRegex(prepareDayFormatForRegex(ISO8601_FORMAT));

/**
 * fns/parse only accepts month as M not m
 * It supports iso, locale, yyyy-mm formats
 */

export type DayFormatter = (day: Day, format?: string) => string;

let formatter: DayFormatter | undefined = undefined;

export const initFormatter = (fn: DayFormatter) => {
	// we could prevent reinit
	formatter = fn;
};

export class Day {
	private rawValue: string;
	private date: Date;

	constructor(year: string | number, month: string | number, date: string | number) {
		this.date = new Date(+year, +month, +date, 0, 0, 0, 0);
		this.rawValue = this.format();
	}

	setRawValue(value: string) {
		this.rawValue = value;
	}

	toISOString(): string {
		return this.format(ISO8601_FORMAT);
	}

	toJSON(): string {
		return this.toISOString();
	}

	toDate() {
		return new Date(this.date);
	}

	isValid() {
		return isValid(this.date);
	}

	getRawValue() {
		return this.rawValue;
	}

	getFullYear() {
		return this.date.getFullYear();
	}

	/**
	 * This is 0-based(!), so for January it will return 0!
	 */
	getMonth() {
		return this.date.getMonth();
	}

	/**
	 * This is 1-based, so for January it will return 1.
	 */
	getISOMonthNumber() {
		return this.date.getMonth() + 1;
	}
	/**
	 * This is 2 digit month format, so for January it will return 01.
	 */
	getISOMonthNumberFormatMM() {
		const month = this.date.getMonth() + 1;
		return (month < 10 ? "0" : "") + month;
	}
	getTime() {
		return this.date.getTime();
	}

	getDayOfMonth() {
		return this.date.getDate();
	}

	getDayOfWeek() {
		return this.date.getDay();
	}

	addYears(amount: number) {
		this.date = addYears(this.date, amount);
		this.rawValue = this.format();
		return this;
	}

	addMonths(amount: number) {
		this.date = addMonths(this.date, amount);
		this.rawValue = this.format();
		return this;
	}

	addDays(amount: number) {
		this.date = addDays(this.date, amount);
		this.rawValue = this.format();
		return this;
	}

	lastDayOfMonth() {
		this.date = lastDayOfMonth(this.date);
		this.rawValue = this.format();
	}

	startDayOfMonth() {
		this.date = startOfMonth(this.date);
		this.rawValue = this.format();
	}

	compare(otherDay: Day) {
		return this.date.getTime() - otherDay.date.getTime();
	}

	/** If format is used, it is one-way you cannot parse it back */
	format(format?: string) {
		if (!formatter) {
			throw new Error("Day formatter not set");
		}
		return formatter(this, format);
	}

	dayDiff(day: Day) {
		return differenceInCalendarDays(this.date, day.date);
	}

	valueOf() {
		return +this.date;
	}

	equals(day: Day) {
		return this.rawValue === day.rawValue;
	}

	static addYears(day: Day, amount: number) {
		return Day.fromDate(addYears(day.date, amount));
	}

	static addMonths(day: Day, amount: number) {
		return Day.fromDate(addMonths(day.date, amount));
	}

	static addDays(day: Day, amount: number | string) {
		return Day.fromDate(addDays(day.date, +amount));
	}

	static yesterday() {
		return Day.fromDate(subDays(new Date(), 1));
	}

	static today(): Day {
		return Day.fromDate(new Date());
	}

	static tomorrow() {
		return Day.fromDate(addDays(Day.today().toDate(), 1));
	}

	static nextFiscalYear() {
		return new Day(Day.today().getFullYear() + 1, 3, 1);
	}

	/**
	 * Use this function to make sure the Day object's `rawValue` is formatted as a local date string.
	 * It can be used to transform an `rawValue` from an ISO date to an local date.
	 */
	formatRawValue() {
		this.rawValue = this.format();
		return this;
	}

	static fromDate(date: Date) {
		return new Day(date.getFullYear(), date.getMonth(), date.getDate());
	}

	static toDate(date: Day) {
		return date.toDate();
	}

	static clone(day: Day) {
		return new Day(day.getFullYear(), day.getMonth(), day.getDayOfMonth());
	}

	static lastDayOfMonth(day: Day) {
		return Day.fromDate(lastDayOfMonth(day.date));
	}

	static startDayOfMonth(day: Day) {
		return Day.fromDate(startOfMonth(day.date));
	}

	static diffInBusinessDays(fromDate: Date, toDate: Date) {
		return differenceInBusinessDays(fromDate, toDate);
	}

	static parseString(value: string, { format = undefined, iso = false }: ParseDateOptions = {}): Day {
		const parsedDate = parseDate(value, { format, iso });
		const day = Day.fromDate(parsedDate);
		day.rawValue = value;
		return day;
	}

	static isValidFormat(value: string, { format, iso }: { format?: string; iso: boolean } = { iso: false }): boolean {
		const supportedFormats: string[] = [];

		if (iso) {
			supportedFormats.push(ISO8601_FORMAT);
		}

		if (format) {
			supportedFormats.push(format);
		} else {
			supportedFormats.push(...getSupportedLocaleFormats(true));
		}

		const supportedRegexes = supportedFormats.map(format => prepareDayFormatForRegex(format));
		return createDayRegex(supportedRegexes.join("|")).test(value);
	}

	static max(...days: [...Day[]]) {
		if (!days.length) {
			throw new Error("Array must contain at least one Day");
		}
		return days.reduce((max, day) => (day.compare(max) > 0 ? day : max));
	}
}

export let dayLocaleFormat = "";

/**
 * fns/parse only accepts month as M not m
 * It supports iso, locale, yyyy-mm formats
 */

export interface ParseDateOptions {
	format?: string;
	iso?: boolean;
}

export function parseDate(value: string, { format = dayLocaleFormat, iso = false }: ParseDateOptions = {}) {
	if (iso) {
		return parseISO(value);
	}

	const reformat = format.replace(/m/g, "M");

	if (reformat.includes("yyyy")) {
		const shortYearFormat = reformat.replace("yyyy", "yy");
		const shortParsed = parse(value, shortYearFormat, new Date());
		if (isValid(shortParsed)) {
			return shortParsed;
		}
	}

	return parse(value, reformat, new Date());
}

export function isWeekEnd(date: Date): boolean {
	const day = date.getDay();

	// Saturday and sunday
	return day === 0 || day === 6;
}

export function addDays(date: Date, days: number): Date {
	const copy = new Date(date.valueOf());
	copy.setDate(copy.getDate() + days);

	return copy;
}

export const getOneYearAgo = (date: Date) => {
	const oneYearAgo = date;
	const previousYear = oneYearAgo.getFullYear() - 1;
	oneYearAgo.setFullYear(previousYear);
	return new Date(oneYearAgo);
};

export function parseDateYYMMDD(value: string, separator: "-" | "", maxDate: Date) {
	const regex = separator === "-" ? /^\d{2}\-\d{2}\-\d{2}$/ : /^\d{2}\d{2}\d{2}$/;
	if (!regex.test(value)) {
		return new Date(NaN);
	}

	const parts =
		separator === "-" ? value.split(separator) : [value.substring(0, 2), value.substring(2, 4), value.substring(4, 6)];
	if (parts.length !== 3) {
		return new Date(NaN);
	}

	const twoDigitYear = Number(parts[0]);
	const monthInYear = Number(parts[1]);
	const dayInMonth = Number(parts[2]);

	if (dayInMonth === 0 || isNaN(dayInMonth)) {
		return new Date(NaN);
	}
	if (monthInYear === 0 || isNaN(monthInYear)) {
		return new Date(NaN);
	}

	if (isNaN(twoDigitYear)) {
		return new Date(NaN);
	}

	let centuryYear = maxDate.getFullYear() - (maxDate.getFullYear() % 100);
	do {
		const date = new Date(centuryYear + twoDigitYear, monthInYear - 1, dayInMonth); // months in Date are 0-11

		if (isNaN(date.getTime())) {
			return date;
		}
		if (date.getTime() < maxDate.getTime()) {
			// valid date check, before returning it
			if (
				date.getFullYear() != centuryYear + twoDigitYear ||
				date.getMonth() != monthInYear - 1 ||
				date.getDate() != dayInMonth
			) {
				return new Date(NaN);
			}
			return date;
		}
		centuryYear -= 100;
	} while (true);
}

export function getSupportedLocaleFormats(truncated = false) {
	const formats = [dayLocaleFormat];
	if (truncated) {
		return [...formats, dayLocaleFormat.replace(/yyyy/, "yy")];
	} else {
		return formats;
	}
}

export function getSupportedLocaleRegex(truncated = false) {
	const formats = getSupportedLocaleFormats(truncated);
	return createDayRegex(formats.map(format => prepareDayFormatForRegex(format)).join("|"));
}

export function setup({ lang }: { lang: string }) {
	if (!lang) {
		return;
	}
	dayLocaleFormat = Intl.DateTimeFormat(lang)
		.formatToParts(new Date(1970, 0, 1))
		.map(part => {
			switch (part.type) {
				case "day":
					return part.value.length === 2 ? "dd" : "d";
				case "month":
					return part.value.length === 2 ? "mm" : "m";
				case "year":
					return "yyyy";
				default:
					return part.value;
			}
		})
		.join("");
}
